/**
 * PagosExcepcionesSection — Tab interno "Excepciones" dentro de Ajustes/Pagos (rc18+).
 *
 * Lista las excepciones de integración Toku (Dead Letter Queue) con detalle:
 *  - Tipo + severidad
 *  - Referencia interna (factura, cliente, pago)
 *  - Error message + acción sugerida
 *  - Acciones: Resolver / Ignorar
 *
 * Permission: integrations.payments.configure (solo admin).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertCircle, AlertTriangle, Info, ShieldAlert,
  Check, X, ChevronRight, Loader2, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiCall, type ApiError } from "@/lib/api";
import { getTz } from "@/lib/tz";

/* ─── Tipos ─────────────────────────────────────────────────── */
type Severidad = "info" | "warning" | "error" | "critical";
type Status = "open" | "in_progress" | "resolved" | "ignored";

type Failure = {
  id: number;
  tipo: string;
  severidad: Severidad;
  internal_factura_id: string | null;
  internal_cliente_id: string | null;
  internal_pago_id: string | null;
  factura_folio: string | null;
  cliente_codigo: string | null;
  cliente_razon_social: string | null;
  // rc51: para huérfanos (sin cliente migrado), backend extrae nombre del raw_payload
  toku_customer_name: string | null;
  toku_customer_id: string | null;
  toku_customer_external_id: string | null;
  toku_event_id: string | null;
  toku_event_type: string | null;
  toku_resource_id: string | null;
  error_message: string;
  error_code: string | null;
  accion_sugerida: string | null;
  status: Status;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  created_at: string;
};

type ListResponse = {
  items: Failure[];
  total: number;
  page: number;
  page_size: number;
  counts_by_status: Record<string, number>;
  counts_by_severidad: Record<string, number>;
};

/* ─── Helpers ───────────────────────────────────────────────── */
const TIPO_LABEL: Record<string, string> = {
  invoice_create_failed: "Factura — falló sync con Toku",
  invoice_void_failed: "Anular factura — falló en Toku",
  payment_notify_failed: "Pago — falló notificar a Toku",
  payment_void_failed: "Anular pago — falló en Toku",
  webhook_no_mapping: "Webhook — sin mapping local",
  webhook_handler_failed: "Webhook — worker agotó reintentos",
  webhook_invalid_hmac: "Webhook — firma HMAC inválida",
  // rc22+: clasificación informativa para pagos externos no-migrados
  external_payment_observed: "Pago observado — cliente no migrado",
  // rc131: Toku no confirmó lo que decía el webhook — NO se aplicó pago
  webhook_verificacion_rechazada: "Webhook — Toku NO confirma el pago",
};

function sevIcon(sev: Severidad) {
  switch (sev) {
    case "critical": return <ShieldAlert size={16} className="text-red-500" />;
    case "error":    return <AlertCircle size={16} className="text-orange-500" />;
    case "warning":  return <AlertTriangle size={16} className="text-yellow-500" />;
    case "info":     return <Info size={16} className="text-blue-500" />;
  }
}

function sevBadge(sev: Severidad) {
  const map: Record<Severidad, string> = {
    critical: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    error:    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    warning:  "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    info:     "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  };
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${map[sev]}`}>{sev.toUpperCase()}</span>;
}

function statusBadge(s: Status) {
  const map: Record<Status, string> = {
    open:        "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    resolved:    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    ignored:     "bg-muted text-muted-foreground",
  };
  const label: Record<Status, string> = {
    open: "Abierta", in_progress: "En proceso",
    resolved: "Resuelta", ignored: "Ignorada",
  };
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${map[s]}`}>{label[s]}</span>;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    timeZone: getTz(),
    dateStyle: "short", timeStyle: "short",
  });
}

/* ─── Hook ──────────────────────────────────────────────────── */
function useFailures(statusFilter: string | null) {
  const qs = statusFilter ? `?status=${statusFilter}` : "";
  return useQuery<ListResponse>({
    queryKey: ["toku-exceptions", statusFilter],
    queryFn: () => apiCall<ListResponse>("get", `/api/v1/integrations/payments/exceptions${qs}`),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

/* ─── Componente ────────────────────────────────────────────── */
export function PagosExcepcionesSection() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string | null>("open");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");

  const { data, isLoading, isError } = useFailures(statusFilter);

  const resolveMut = useMutation({
    mutationFn: ({ id, action, notes }: { id: number; action: "resolve" | "ignore"; notes: string }) =>
      apiCall("post", `/api/v1/integrations/payments/exceptions/${id}/${action}`, {
        json: { resolution_notes: notes || null },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["toku-exceptions"] });
      toast.success(vars.action === "resolve" ? "Marcada como resuelta" : "Ignorada");
      setSelectedId(null);
      setResolutionNotes("");
    },
    onError: (e: ApiError) => toast.error(e.detail || "Error"),
  });

  if (isLoading) return <div className="text-xs text-muted-foreground">Cargando excepciones…</div>;
  if (isError || !data) return <div className="text-xs text-destructive">Error cargando excepciones</div>;

  const selected = selectedId !== null
    ? data.items.find(f => f.id === selectedId) || null
    : null;

  const counts = data.counts_by_severidad;
  const totalOpen = (counts.critical || 0) + (counts.error || 0) + (counts.warning || 0) + (counts.info || 0);

  return (
    <div className="flex flex-col gap-3">
      {/* Counter summary */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SummaryCard label="Críticas abiertas" count={counts.critical || 0} icon={<ShieldAlert size={14} />} color="text-red-500" />
        <SummaryCard label="Errores abiertos" count={counts.error || 0} icon={<AlertCircle size={14} />} color="text-orange-500" />
        <SummaryCard label="Warnings abiertas" count={counts.warning || 0} icon={<AlertTriangle size={14} />} color="text-yellow-500" />
        <SummaryCard label="Total abiertas" count={totalOpen} icon={<Info size={14} />} color="text-primary" />
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b">
        {[
          { value: "open", label: "Abiertas", count: data.counts_by_status.open || 0 },
          { value: "resolved", label: "Resueltas", count: data.counts_by_status.resolved || 0 },
          { value: "ignored", label: "Ignoradas", count: data.counts_by_status.ignored || 0 },
          { value: null, label: "Todas", count: data.total },
        ].map(t => (
          <button
            key={t.value || "all"}
            onClick={() => setStatusFilter(t.value)}
            className={`px-3 py-1.5 text-xs font-medium ${
              statusFilter === t.value
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label} <span className="ml-1 text-[10px] opacity-60">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Empty state */}
      {data.items.length === 0 && (
        <div className="rounded border border-dashed bg-card p-6 text-center">
          <Check size={24} className="mx-auto mb-2 text-green-500" />
          <p className="text-sm font-medium">Sin excepciones {statusFilter === "open" ? "abiertas" : "en este filtro"}</p>
          <p className="text-xs text-muted-foreground">La integración Toku está al día.</p>
        </div>
      )}

      {/* List */}
      {data.items.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {data.items.map(f => (
            <button
              key={f.id}
              onClick={() => setSelectedId(f.id)}
              className="flex items-start justify-between gap-3 rounded border bg-card p-3 text-left transition-colors hover:bg-accent"
            >
              <div className="flex flex-1 items-start gap-2">
                <span className="mt-0.5">{sevIcon(f.severidad)}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{TIPO_LABEL[f.tipo] || f.tipo}</span>
                    {sevBadge(f.severidad)}
                    {statusBadge(f.status)}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                    {f.error_message}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                    {f.factura_folio && <span>📄 {f.factura_folio}</span>}
                    {f.cliente_codigo && <span>👤 {f.cliente_codigo} · {f.cliente_razon_social}</span>}
                    {/* rc51: huérfanos Toku — mostrar nombre del cliente del payload */}
                    {!f.cliente_codigo && f.toku_customer_name && (
                      <span title="Cliente no migrado a ByteISP">
                        👤 {f.toku_customer_name}
                        {f.toku_customer_external_id && <span className="opacity-60"> · ext:{f.toku_customer_external_id}</span>}
                      </span>
                    )}
                    {f.toku_event_id && <span className="font-mono">⚡ {f.toku_event_id.slice(0, 20)}...</span>}
                    <span>· {fmtDate(f.created_at)}</span>
                  </div>
                </div>
              </div>
              <ChevronRight size={16} className="mt-1 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelectedId(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg bg-card p-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {sevIcon(selected.severidad)}
                  <h3 className="text-sm font-semibold">{TIPO_LABEL[selected.tipo] || selected.tipo}</h3>
                  {sevBadge(selected.severidad)}
                  {statusBadge(selected.status)}
                </div>
                <p className="text-xs text-muted-foreground">ID #{selected.id} · {fmtDate(selected.created_at)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
                <X size={16} />
              </Button>
            </div>

            <div className="space-y-3 text-xs">
              <DetailRow label="Error" value={selected.error_message} mono />
              {selected.accion_sugerida && (
                <div className="rounded border border-primary/30 bg-primary/5 p-2">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-primary">Acción sugerida</div>
                  <div className="text-xs">{selected.accion_sugerida}</div>
                </div>
              )}
              {(selected.factura_folio || selected.cliente_codigo || selected.toku_customer_name) && (
                <div className="grid grid-cols-2 gap-3">
                  {selected.factura_folio && <DetailRow label="Factura" value={selected.factura_folio} />}
                  {selected.cliente_codigo ? (
                    <DetailRow label="Cliente" value={`${selected.cliente_codigo} — ${selected.cliente_razon_social || ""}`} />
                  ) : selected.toku_customer_name && (
                    <DetailRow
                      label="Cliente Toku (no migrado)"
                      value={`${selected.toku_customer_name}${selected.toku_customer_external_id ? ` — ext:${selected.toku_customer_external_id}` : ""}`}
                    />
                  )}
                </div>
              )}
              {selected.toku_event_id && (
                <DetailRow label="Evento Toku" value={`${selected.toku_event_type || "?"} (${selected.toku_event_id})`} mono />
              )}
              {selected.error_code && <DetailRow label="Código de error" value={selected.error_code} mono />}
              {selected.resolution_notes && (
                <DetailRow label="Notas de resolución" value={selected.resolution_notes} />
              )}
            </div>

            {selected.status === "open" && (
              <div className="mt-4 border-t pt-3 space-y-2">
                <label className="text-xs font-medium">Notas (opcional)</label>
                <textarea
                  value={resolutionNotes}
                  onChange={e => setResolutionNotes(e.target.value)}
                  placeholder="Qué hiciste para resolver, o por qué se ignora…"
                  rows={2}
                  className="w-full rounded border px-2 py-1 text-xs"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resolveMut.mutate({ id: selected.id, action: "ignore", notes: resolutionNotes })}
                    disabled={resolveMut.isPending}
                  >
                    Ignorar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => resolveMut.mutate({ id: selected.id, action: "resolve", notes: resolutionNotes })}
                    disabled={resolveMut.isPending}
                  >
                    {resolveMut.isPending ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Check size={12} className="mr-1" />}
                    Marcar resuelta
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────── */
function SummaryCard({ label, count, icon, color }: { label: string; count: number; icon: React.ReactNode; color: string }) {
  return (
    <div className="rounded border bg-card p-3">
      <div className={`flex items-center gap-1.5 ${color}`}>
        {icon}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold">{count}</div>
    </div>
  );
}

function DetailRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? "mt-0.5 font-mono text-[11px] break-all" : "mt-0.5 break-words"}>{value}</div>
    </div>
  );
}
